// Helper function to deep merge groupedByCategoryAndBusiness objects
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DayEntry } from '../BookEntries/components';

dayjs.extend(utc);

export const deepMergeGroupedData = (
  objects: Record<string, unknown>[],
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  objects.forEach((obj) => {
    if (!obj) return;
    Object.keys(obj).forEach((key) => {
      if (result[key]) {
        // If both are objects, merge them
        if (
          typeof result[key] === 'object' &&
          typeof obj[key] === 'object' &&
          !Array.isArray(result[key]) &&
          !Array.isArray(obj[key])
        ) {
          result[key] = { ...result[key], ...obj[key] };
        } else {
          // Otherwise, use the new value (or merge arrays if needed)
          result[key] = obj[key];
        }
      } else {
        result[key] = obj[key];
      }
    });
  });

  return result;
};

interface SummedLine {
  account: string;
  accountName?: string;
  debit?: number;
  credit?: number;
  debtor?: number;
  creditor?: number;
  active?: number;
  passive?: number;
  loss?: number;
  income?: number;
  accountingLines?: unknown[];
  [key: string]: unknown;
}

// Helper function to merge summedLines arrays (sum numeric values for same account)
export const mergeSummedLines = (
  summedLinesArrays: SummedLine[][],
): SummedLine[] => {
  const accountMap = new Map<string, SummedLine>();

  summedLinesArrays.forEach((summedLines) => {
    summedLines.forEach((line) => {
      const key = line.account || '';
      if (accountMap.has(key)) {
        // Merge by summing numeric values
        const existing = accountMap.get(key)!;
        existing.debit = (existing.debit || 0) + (line.debit || 0);
        existing.credit = (existing.credit || 0) + (line.credit || 0);
        existing.debtor = (existing.debtor || 0) + (line.debtor || 0);
        existing.creditor = (existing.creditor || 0) + (line.creditor || 0);
        existing.active = (existing.active || 0) + (line.active || 0);
        existing.passive = (existing.passive || 0) + (line.passive || 0);
        existing.loss = (existing.loss || 0) + (line.loss || 0);
        existing.income = (existing.income || 0) + (line.income || 0);
        // Merge accountingLines if they exist
        if (line.accountingLines) {
          existing.accountingLines = [
            ...(existing.accountingLines || []),
            ...(line.accountingLines || []),
          ];
        }
      } else {
        accountMap.set(key, { ...line });
      }
    });
  });

  return Array.from(accountMap.values());
};

interface GroupedLine {
  date: string;
  businessIdentifier: string;
  businessName: string;
  account?: string;
  accountName?: string;
  description?: string;
  documentId?: string;
  debit?: number;
  credit?: number;
  debitFinal?: number;
  creditFinal?: number;
  [key: string]: unknown;
}

interface MajorBookAccount {
  account: string;
  accountName?: string;
  groupedLines: GroupedLine[];
  [key: string]: unknown;
}

// Helper function to merge majorBookAccounts arrays
export const mergeMajorBookAccounts = (
  accountsArrays: MajorBookAccount[][],
): MajorBookAccount[] => {
  const accountMap = new Map<string, MajorBookAccount>();

  accountsArrays.forEach((accounts) => {
    accounts.forEach((account) => {
      const key = account.account || '';
      if (accountMap.has(key)) {
        // Merge groupedLines
        const existing = accountMap.get(key)!;
        existing.groupedLines = [
          ...(existing.groupedLines || []),
          ...(account.groupedLines || []),
        ];
      } else {
        accountMap.set(key, { ...account });
      }
    });
  });

  return Array.from(accountMap.values());
};

export const mergeGroupedLinesToDateList = (
  dateLists: DayEntry[][],
): DayEntry[] => {
  const dateMap = new Map<string, DayEntry>();

  dateLists.forEach((dateList) => {
    dateList.forEach((day: DayEntry) => {
      const dateKey = day.date || '';
      if (dateMap.has(dateKey)) {
        const existing = dateMap.get(dateKey)!;
        existing.groupedLinesByCategoryAndBusiness = [
          ...(existing.groupedLinesByCategoryAndBusiness || []),
          ...(day.groupedLinesByCategoryAndBusiness || []),
        ];
      } else {
        dateMap.set(dateKey, { ...day });
      }
    });
  });

  return Array.from(dateMap.values()).sort((a, b) => {
    return dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
  });
};
